/**
 * Country -> cable sizing standard mapping for the public calculators (CPW-5547).
 *
 * Kept free of Angular and HTTP imports so it stays a pure, trivially testable module.
 */

/**
 * Identifies a cable sizing standard. These are exactly the `code` values in
 * WebfacingHeaderV2Component.standardVariants, so the compiler prevents the mapping table
 * below and the header dropdown from ever drifting apart.
 */
export type StandardCode = 'as' | 'bs' | 'iec' | 'nz' | 'nec';

/** AS/NZS 3008.1.1 - the site default, used for unmapped countries and failed lookups. */
export const DEFAULT_STANDARD_CODE: StandardCode = 'as';

/**
 * 'detected' - the visitor's country was resolved AND appears in COUNTRY_TO_STANDARD.
 * 'default'  - unmapped country OR the lookup failed.
 *
 * This distinction is load-bearing and cannot be inferred from standardCode alone: an
 * Australian visitor and a failed lookup both yield 'as', but only the former may trigger the
 * popup (e.g. an AU visitor sitting on the NEC page). 'default' means no popup and no reorder.
 */
export type DetectionStatus = 'detected' | 'default';

export interface RegionalDetectionResult {
  status: DetectionStatus;
  /** ISO 3166-1 alpha-2, e.g. 'US'. Null when status is 'default'. */
  countryCode: string | null;
  /** Full country name for popup copy, e.g. 'United States'. Null when status is 'default'. */
  countryName: string | null;
  /** Always populated; DEFAULT_STANDARD_CODE when status is 'default'. */
  standardCode: StandardCode;
}

/**
 * Per the CPW-5547 mapping table. Countries absent from this map fall back to
 * DEFAULT_STANDARD_CODE with status 'default' - no popup, no dropdown reorder.
 *
 * Note AU and NZ are deliberately distinct: AS/NZS 3008.1.1 vs AS/NZS 3008.1.2.
 * CA -> NEC is per the ticket (pending George's confirmation of the asterisk against Canada).
 */
export const COUNTRY_TO_STANDARD: Readonly<Record<string, StandardCode>> = {
  AU: 'as',
  NZ: 'nz',
  GB: 'bs',
  US: 'nec',
  CA: 'nec',
  // Europe
  DE: 'iec', FR: 'iec', ES: 'iec', IT: 'iec', NL: 'iec', BE: 'iec', AT: 'iec', CH: 'iec',
  SE: 'iec', NO: 'iec', DK: 'iec', FI: 'iec', IE: 'iec', PL: 'iec', PT: 'iec', CZ: 'iec',
  RO: 'iec',
  // Asia, Middle East, Africa, South America
  SG: 'iec', MY: 'iec', IN: 'iec', AE: 'iec', SA: 'iec', QA: 'iec', KW: 'iec', ZA: 'iec',
  BR: 'iec', CL: 'iec', PE: 'iec'
};

/**
 * Standard names as they appear in the popup title and buttons, per modal-prototype-v2.
 *
 * Deliberately its own map rather than reusing the header dropdown's `name` field: that field
 * reads 'AS/NZS 3008 (NZ)' and 'NFPA 70 (NEC)', which is not the wording the prototype uses.
 * The dropdown's `label` ('NEC NFPA 70 (United States)') is longer still.
 */
export const STANDARD_DISPLAY_NAME: Readonly<Record<StandardCode, string>> = {
  as: 'AS/NZS 3008.1.1',
  nz: 'AS/NZS 3008.1.2',
  bs: 'BS 7671',
  iec: 'IEC 60364-5-52',
  nec: 'NEC NFPA 70'
};

/** The result used whenever detection did not produce a mapped country. */
export const DEFAULT_DETECTION_RESULT: RegionalDetectionResult = {
  status: 'default',
  countryCode: null,
  countryName: null,
  standardCode: DEFAULT_STANDARD_CODE
};

/** Maps an ISO alpha-2 country code to a standard, or null when the country is not mapped. */
export function standardCodeForCountry(countryCode: string | null | undefined): StandardCode | null {
  if (!countryCode) {
    return null;
  }
  return COUNTRY_TO_STANDARD[countryCode.trim().toUpperCase()] ?? null;
}
